import React, { useState, useEffect } from "react";
import Modal from "react-bootstrap/Modal";
import { Table } from "react-bootstrap";
import XLSX from "xlsx";
import { CSVLink } from "react-csv";
import { getLeadInfo } from "../../services/zohoService";
import months from "../../utils/months";
import headers from "./reportHeaders";
import UploadIcon from "../../assets/images/upload-icon.svg";
import DownloadIcon from "../../assets/icons/download.svg";
import "./ReportsModal.scss";

const ReportsModal = ({ show, onHide }) => {
  const date = new Date();
  const currentYear = date.getFullYear();

  const [formData, setFormData] = useState({
    cadToUsd: 1,
    commissionMonth: `${currentYear}-1-1`,
    and: "ANET",
  });

  const [currentMonth, setCurrentMonth] = useState(`${currentYear - 1}-12-1`);
  const [downloadableFile, setDownloadableFile] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingSummary, setProcessingSummary] = useState(null);

  // Reset function to clear all data
  const resetModal = () => {
    setFormData({
      cadToUsd: 1,
      commissionMonth: `${currentYear}-1-1`,
      and: "ANET",
    });
    setCurrentMonth(`${currentYear - 1}-12-1`);
    setDownloadableFile([]);
    setIsProcessing(false);
    setProcessingSummary(null);
  };

  // Reset modal when it closes
  useEffect(() => {
    if (!show) {
      resetModal();
    }
  }, [show]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFileUpload = async (e) => {
    if (e.target.files.length < 1) return;

    // Validate form data
    if (!formData.cadToUsd || formData.cadToUsd <= 0) {
      alert("Please enter a valid CAD to USD conversion rate.");
      return;
    }

    if (!formData.commissionMonth) {
      alert("Please select a commission month.");
      return;
    }

    if (!formData.and) {
      alert("Please select a company.");
      return;
    }

    setIsProcessing(true);
    setDownloadableFile([]);

    const startTime = Date.now();
    const minLoadingTime = 2000; // Minimum 2 seconds loading time

    const files = e.target.files;
    const f = files[0];
    const reader = new FileReader();

    reader.onload = async function (e) {
      try {
        const data = e.target.result;
        let readedData = XLSX.read(data, { type: "binary" });
        const wsname = readedData.SheetNames[0];
        const ws = readedData.Sheets[wsname];

        /* Convert array to json*/
        const dataParse = XLSX.utils.sheet_to_json(ws, {
          header: 1,
          defval: null,
        });

        await makeTableBody(dataParse);

        // Ensure minimum loading time
        const elapsedTime = Date.now() - startTime;
        if (elapsedTime < minLoadingTime) {
          await new Promise((resolve) =>
            setTimeout(resolve, minLoadingTime - elapsedTime)
          );
        }

        setIsProcessing(false);
      } catch (error) {
        console.error("Error processing file:", error);
        setIsProcessing(false);
        alert(
          "Error processing file. Please make sure it's a valid Excel file."
        );
      }
    };

    reader.onerror = function () {
      console.error("Error reading file");
      setIsProcessing(false);
      alert("Error reading file. Please try again.");
    };

    reader.readAsBinaryString(f);
  };

  const makeTableHeader = () => {
    if (formData.and === "CYBS") {
      return (
        <tr>
          {headers.headers.map((header, index) => (
            <th key={index}>{header}</th>
          ))}
        </tr>
      );
    }
    if (formData.and === "ANET") {
      return (
        <tr>
          {headers.anetHeaders.map((header, index) => (
            <th key={index}>{header}</th>
          ))}
        </tr>
      );
    }
  };

  const makeTableBody = async (file) => {
    let mid,
      cybsMid,
      merchantName,
      currency,
      grossSales,
      trans,
      totalIncome,
      totalExpense,
      agentNet;
    const revShareType = "Percent_of_Rev";
    const revShareVariable = 0.5;
    
    let totalMerchants = 0;
    let processedMerchants = 0;
    let missingDataMerchants = 0;

    // Process headers first
    if (file.length > 0) {
      file[0].forEach((title, index) => {
        if (title === "Merchant ID" || title === "MID") {
          mid = index;
        }
        if (title === "Merchant" || title === "DBA") {
          merchantName = index;
        }
        if (title === "Transactions" || title === "Net No") {
          trans = index;
        }
        if (title === "Sales Amount" || title === "Net Volume") {
          grossSales = index;
        }
        if (title === "Income" || title === "Gross Rev") {
          totalIncome = index;
        }
        if (title === "Agent Net" || title === "Total Residual Earnings") {
          agentNet = index;
        }
        if (title === "Expenses" || title === "Gross Exp") {
          totalExpense = index;
        }
      });
    }

    // Process each merchant row
    totalMerchants = file.slice(1).length;
    const promises = file.slice(1).map(async (merchant, index) => {
      try {
        if (Number(merchant[mid])) {
          let income = merchant[totalIncome];
          let incomeToCybs = merchant[agentNet] * revShareVariable;
          let grossSalesAmount = merchant[grossSales];
          let totalExpenseAmount = merchant[totalExpense];
          let grossProfit = merchant[agentNet];
          let platformType,
            processor,
            approvedDate,
            mcc,
            basisPoints,
            perTransFee,
            startBpsCost;

          const data = await getLeadInfo(merchant[mid]);
          cybsMid = data?.Gateway_ID || "Update MID";
          processor = data?.Processor;
          approvedDate = data?.Date_Approved;
          mcc = data?.MCC;
          currency = data?.Processing_Currency || "no mid";
          platformType = data?.GW_Platform || "Update MID";
          basisPoints = data?.Basis_Points;
          perTransFee = data?.Per_Transaction;
          startBpsCost = data?.START_BPS_Cost;

          if (merchant[totalIncome] === 0) {
            income = merchant[agentNet] + merchant[totalExpense];
            income = Math.round(100 * income) / 100;
          }
          if (currency === "CAD") {
            income = income * formData.cadToUsd;
            income = Math.round(100 * income) / 100;
            incomeToCybs = grossProfit * formData.cadToUsd * revShareVariable;
            incomeToCybs = Math.round(100 * incomeToCybs) / 100;
            grossSalesAmount = grossSalesAmount * formData.cadToUsd;
            grossSalesAmount = Math.round(100 * grossSalesAmount) / 100;
            totalExpenseAmount = totalExpenseAmount * formData.cadToUsd;
            totalExpenseAmount = Math.round(100 * totalExpenseAmount) / 100;
            grossProfit = grossProfit * formData.cadToUsd;
            grossProfit = Math.round(100 * grossProfit) / 100;
          }
          if (currency === "no mid") {
            income = 0;
            incomeToCybs = 0;
            grossSalesAmount = 0;
            totalExpenseAmount = 0;
            grossProfit = 0;
          }

          if (formData.and === "CYBS") {
            processedMerchants++;
            return [
              merchant[mid],
              cybsMid,
              platformType,
              merchant[merchantName],
              currentMonth,
              formData.commissionMonth,
              currency,
              grossSalesAmount,
              merchant[trans],
              basisPoints,
              perTransFee,
              startBpsCost,
              income,
              totalExpenseAmount,
              grossProfit,
              revShareType,
              revShareVariable,
              incomeToCybs,
            ];
          }
          if (formData.and === "ANET") {
            processedMerchants++;
            return [
              merchant[mid],
              cybsMid,
              platformType,
              merchant[merchantName],
              currentMonth,
              formData.commissionMonth,
              currency,
              grossSalesAmount,
              merchant[trans],
              basisPoints,
              perTransFee,
              startBpsCost,
              income,
              totalExpenseAmount,
              grossProfit,
              revShareType,
              revShareVariable,
              incomeToCybs,
              processor,
              approvedDate,
              mcc,
            ];
          }
        } else {
          missingDataMerchants++;
        }
        return null;
      } catch (error) {
        console.error(error);
        return null;
      }
    });

    // Wait for all promises to resolve
    const results = await Promise.all(promises);
    const validResults = results.filter((result) => result !== null);

    // Set processing summary
    setProcessingSummary({
      total: totalMerchants,
      processed: processedMerchants,
      missing: missingDataMerchants
    });

    setDownloadableFile(validResults);
    setIsProcessing(false);
  };

  const renderBody = () => {
    return downloadableFile.map((merchant, index) => {
      const redStyle = merchant[1] === "Update MID";
      return (
        <tr
          key={index}
        >
          {merchant.map((i, cellIndex) => (
            <td key={cellIndex}>{i}</td>
          ))}
        </tr>
      );
    });
  };

  const handleCommissionMonthChange = (e) => {
    const selectedMonth = e.target.value;
    const monthNumber = selectedMonth.split("-")[1];
    const arrayValueOfMonth = months.indexOf(monthNumber);

    setFormData((prev) => ({
      ...prev,
      commissionMonth: selectedMonth,
    }));

    if (arrayValueOfMonth === 0) {
      setCurrentMonth(`${currentYear - 1}-${months[11]}-1`);
    } else {
      setCurrentMonth(`${currentYear}-${months[arrayValueOfMonth - 1]}-1`);
    }
  };

  const download =
    formData.and === "CYBS"
      ? [headers.headers, ...downloadableFile]
      : [headers.anetHeaders, ...downloadableFile];

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="xl"
      aria-labelledby="reportsModalLabel"
      className="reports-modal"
    >
      <Modal.Header closeButton className="border-0">
        <Modal.Title id="reportsModalLabel">
          <h1>Reports</h1>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <div className="row">
          <div className="col-12 col-md-6 col-lg-4 mb-3">
            <div className="form-floating mb-3">
              <input
                className="form-control"
                type="number"
                name="cadToUsd"
                id="cadToUsd"
                value={formData.cadToUsd}
                onChange={handleInputChange}
                step="0.01"
                min="0"
              />
              <label htmlFor="cadToUsd">CAD to USD @</label>
            </div>
          </div>

          <div className="col-12 col-md-6 col-lg-4 mb-3">
            <div className="form-floating mb-3">
              <select
                className="form-select"
                name="commissionMonth"
                value={formData.commissionMonth}
                onChange={handleCommissionMonthChange}
              >
                {months.map((month) => (
                  <option key={month} value={`${currentYear}-${month}-1`}>
                    {`${currentYear}-${month}-1`}
                  </option>
                ))}
              </select>
              <label htmlFor="floatingInput">Select Commission Month</label>
            </div>
          </div>

          <div className="col-12 col-md-6 col-lg-4 mb-3">
            <div className="form-floating mb-3">
              <select
                className="form-select"
                name="and"
                value={formData.and}
                onChange={handleInputChange}
              >
                <option value="CYBS">CYBS</option>
                <option value="ANET">ANET</option>
              </select>
              <label htmlFor="floatingInput">and</label>
            </div>
          </div>

          <div className="col-12 mb-3">
            <div className="upload-file">
              <label className="file">
                <div>
                  <img className="mb-2" src={UploadIcon} alt="Upload" />
                </div>
                <div>
                  Drag & Drop Files or <span>Browse</span>
                </div>
                <input type="file" onChange={handleFileUpload} />
              </label>
            </div>
          </div>

          {/* Processing Indicator */}
          {isProcessing && (
            <div className="col-12 mb-3">
              <div className="processing-indicator">
                <div className="text-center p-4">
                  <div
                    className="spinner-border text-success mb-3"
                    style={{ width: "3rem", height: "3rem" }}
                    role="status"
                  >
                    <span className="visually-hidden">Processing...</span>
                  </div>
                  <div className="text-success fw-bold fs-5 mb-2">
                    <i className="bi bi-gear-fill me-2"></i>
                    Processing File...
                  </div>
                  <p className="text-muted mb-0">
                    Please wait while we analyze your Excel data
                  </p>
                  <div className="progress mt-3" style={{ height: "6px" }}>
                    <div
                      className="progress-bar progress-bar-striped progress-bar-animated bg-success"
                      style={{ width: "100%" }}
                    ></div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Processing Summary */}
          {processingSummary && !isProcessing && (
            <div className="col-12 mb-3">
              <div className="processing-summary">
                <div className="summary-header">
                  <i className="bi bi-check-circle-fill text-success me-2"></i>
                  <span className="fw-bold">Processing Complete</span>
                </div>
                <div className="summary-stats">
                  <div className="stat-item">
                    <span className="stat-number text-success">{processingSummary.processed}</span>
                    <span className="stat-label">Processed</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-number text-warning">{processingSummary.missing}</span>
                    <span className="stat-label">Missing Data</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-number text-info">{processingSummary.total}</span>
                    <span className="stat-label">Total Records</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Data Table */}
          <div className="col-12 reports-modal-table-container">
            <div className="reports-modal-table-wrapper">
              <Table size="sm">
                <thead>{makeTableHeader()}</thead>
                <tbody>
                  {downloadableFile.length > 0 ? (
                    renderBody()
                  ) : (
                    <tr>
                      <td 
                        colSpan={formData.and === "CYBS" ? headers.headers.length : headers.anetHeaders.length}
                        className="text-center text-muted py-4"
                      >
                        <div className="d-flex flex-column align-items-center">
                          <i className="bi bi-upload fs-1 mb-2 text-muted"></i>
                          <p className="mb-0">Upload an Excel file to see the data</p>
                          <small className="text-muted">Supported formats: .xlsx, .xls</small>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          </div>
        </div>
      </Modal.Body>

      <Modal.Footer className="border-0">
        <div className="d-flex gap-3 w-100">
          <button
            type="button"
            className="btn btn-outline-secondary flex-fill modern-btn"
            onClick={resetModal}
            disabled={downloadableFile.length === 0}
          >
            <i className="bi bi-arrow-clockwise me-2"></i>
            Reset
          </button>
          {downloadableFile.length > 0 ? (
            <CSVLink
              data={download}
              target="_blank"
              filename={`Monthly-Report-${formData.and}-${formData.commissionMonth}.csv`}
              className="btn btn-success flex-fill d-flex align-items-center justify-content-center modern-btn"
            >
              <img src={DownloadIcon} alt="Download" className="me-2 download-icon-white" style={{ width: "18px", height: "18px" }} />
              Download Report
            </CSVLink>
          ) : (
            <button
              type="button"
              className="btn btn-success flex-fill d-flex align-items-center justify-content-center modern-btn"
              disabled
            >
              <img src={DownloadIcon} alt="Download" className="me-2 download-icon-white" style={{ width: "18px", height: "18px" }} />
              Download Report
            </button>
          )}
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default ReportsModal;
